import { useLazyGetWorkspaceFiltersQuery } from '@carboncopyco/data';
import { TableViewIcon, ThumbnailsViewIcon } from '@carboncopyco/icons';
import type { Entity } from '@carboncopyco/types';
import {
  Container,
  type DropdownFilterItemContent,
  MultiDropdownFilter,
  RadioGroupButton,
  renderCreatorItem,
  renderEntityItem,
} from '@carboncopyco/ui';
import { capitalize } from '@carboncopyco/utils';
import { useUpdateEffect } from 'ahooks';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { SearchInput, SortDropdown } from '../../UrlStateControls';
import type { ViewMode } from '../../types';
import { useUrlState } from '../../utils';
import { useWorkspacePageProps } from './WorkspacePageContext';
import {
  ALL_ENTITIES,
  INITIAL_URL_STATE,
  SHARED_FOLDER_ENTITIES,
} from './consts';
import type { UrlState } from './types';

export const WorkspacePageControls = forwardRef<
  { isDirty: boolean; resetControls?: () => void },
  { archive?: boolean; readOnly?: boolean }
>(({ archive, readOnly }, ref) => {
  const { organizationId, workspaceId } = useWorkspacePageProps();
  const [getWorkspaceFilters, { data: filters }] =
    useLazyGetWorkspaceFiltersQuery();
  const [urlState, setUrlState] = useUrlState<UrlState>(INITIAL_URL_STATE);
  const { query, creators, entities, viewMode } = urlState;
  const isDirty = !!(query || creators || entities);
  const searchInputRef = useRef<{
    resetSearchInput: () => void;
  }>();

  useImperativeHandle(ref, () => ({
    isDirty: isDirty,
    resetControls: () => {
      searchInputRef.current?.resetSearchInput();
      setUrlState({
        ...urlState,
        query: undefined,
        creators: undefined,
        entities: undefined,
      });
    },
  }));

  useEffect(() => {
    getWorkspaceFilters({
      organizationId: Number(organizationId),
      entities: (readOnly ? SHARED_FOLDER_ENTITIES : ALL_ENTITIES) as Entity[],
      query,
      workspaceId,
      deletedEntities: archive,
    });
  }, [
    readOnly,
    archive,
    getWorkspaceFilters,
    organizationId,
    query,
    workspaceId,
  ]);

  useEffect(() => {
    searchInputRef.current?.resetSearchInput();
  }, [workspaceId]);

  useUpdateEffect(() => {
    setUrlState({
      entities: undefined,
      creators: undefined,
    });
  }, [organizationId, query, workspaceId]);

  const creatorOptions =
    filters?.creators?.map((creator) => ({
      id: creator.userId,
      label: capitalize(creator.userName),
      image: creator.image,
    })) || [];

  const typeOptions: Array<DropdownFilterItemContent<Entity>> = [
    { label: 'Demo', id: 'demo' },
    { label: 'Tour', id: 'tour' },
    {
      label: 'Playbook',
      id: 'playbook',
    },
  ];

  if (!readOnly) {
    typeOptions.push({ label: 'Clone', id: 'record' });
  }

  return (
    <Container partialHeight>
      <div className="">
        <div className="w-full flex justify-between py-4">
          <div className="flex gap-3">
            <SortDropdown />
            <MultiDropdownFilter
              placeholder="Created by"
              fixedGroup={{ items: creatorOptions }}
              renderItem={renderCreatorItem}
              selectedItems={
                creators
                  ? Object.fromEntries(
                      creators
                        .split(',')
                        .map((optionId) => [
                          optionId,
                          creatorOptions.find(
                            (option) => option.id === optionId
                          )?.label,
                        ])
                    )
                  : {}
              }
              onChange={(optionId) => {
                const optionsSet = new Set(
                  creators?.split(',').map((option) => option.trim())
                );
                if (optionsSet.has(optionId)) {
                  optionsSet.delete(optionId);
                } else {
                  optionsSet.add(optionId);
                }
                setUrlState({
                  ...urlState,
                  creators: Array.from(optionsSet.values()).join(','),
                });
              }}
              onClear={() =>
                setUrlState({
                  ...urlState,
                  creators: undefined,
                })
              }
            />
            <MultiDropdownFilter
              placeholder="All types"
              fixedGroup={{ items: typeOptions }}
              renderItem={renderEntityItem}
              selectedItems={
                entities
                  ? Object.fromEntries(
                      entities
                        .split(',')
                        .map((optionId) => [
                          optionId,
                          typeOptions.find((option) => option.id === optionId)
                            ?.label,
                        ])
                    )
                  : {}
              }
              onChange={(optionId) => {
                const optionsSet = new Set(
                  entities?.split(',').map((option) => option.trim())
                );
                if (optionsSet.has(optionId)) {
                  optionsSet.delete(optionId);
                } else {
                  optionsSet.add(optionId);
                }
                setUrlState({
                  ...urlState,
                  entities: Array.from(optionsSet.values()).join(','),
                });
              }}
              onClear={() =>
                setUrlState({
                  ...urlState,
                  entities: undefined,
                })
              }
            />
          </div>
          <div className="flex gap-3">
            <div className="w-ds-66">
              <SearchInput ref={searchInputRef} />
            </div>

            <RadioGroupButton
              buttonsConfig={[
                {
                  id: 'tiles',
                  icon: <ThumbnailsViewIcon />,
                  tooltip: true,
                  tooltipTitle: 'Tile view',
                },
                {
                  id: 'table',
                  icon: <TableViewIcon />,
                  tooltip: true,
                  tooltipTitle: 'List view',
                },
              ]}
              onClick={(selected: ViewMode) => {
                setUrlState({
                  ...urlState,
                  viewMode: selected,
                });
              }}
              selected={viewMode}
            />
          </div>
        </div>
      </div>
    </Container>
  );
});

WorkspacePageControls.displayName = 'WorkspacePageControls';
